import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

export const HappyHumanToolsApp: React.FC = () => {
  const navigate = useNavigate();

  const tools = [
    {
      id: 'd1-upload-links',
      title: '💾 D1 Upload Link Manager',
      description: 'CloudFlare D1 database integration with step-by-step visualization',
      features: ['Single table demo', 'CRUD operations', 'Data flow tracking', 'Mock mode fallback'],
      path: '/d1-upload-links',
      status: 'live'
    },
    {
      id: 'business-files',
      title: '🏢 Business File Sharing',
      description: 'Simple, memorable URLs for clients to upload files',
      features: ['Custom business URLs', 'Live URL preview', 'Password protection', 'Upload tracking'],
      path: '/business-files',
      status: 'live'
    },
    {
      id: 'business-files-steps',
      title: '⚙️ File Sharing Technical Steps',
      description: 'See every database operation and storage step',
      features: ['D1 database operations', 'R2 storage process', 'Route creation steps', 'File listing flow'],
      path: '/business-files-steps',
      status: 'live'
    },
    {
      id: 'business-files-interactive',
      title: '🎮 Interactive File Sharing',
      description: 'Actually create routes, upload files, and manage everything',
      features: ['Real route creation', 'File upload tracking', 'Action log with undo', 'Clear all data'],
      path: '/business-files-interactive',
      status: 'live'
    },
    {
      id: 'email',
      title: '📧 Email Platform',
      description: 'AWS SES email system with CloudFlare Workers',
      features: ['Send emails via API', 'Domain verification', 'Real-time tracking'],
      path: '/send-email',
      status: 'live'
    },
    {
      id: 'files',
      title: '📁 File Manager',
      description: 'R2 storage with secure client folder system',
      features: ['Upload/download files', 'Client folders', 'Share links'],
      path: '/files',
      status: 'live'
    },
    {
      id: 'share-links',
      title: '🔗 Shareable Upload Links',
      description: 'Create links that let anyone upload files to your storage',
      features: ['Unique upload links', 'Password protection', 'Upload tracking', 'Expiration controls'],
      path: '/share-links',
      status: 'live'
    },
    {
      id: 'deployment',
      title: '🚀 Deployment',
      description: 'Step-by-step deployment visualization',
      features: ['CloudFlare Workers', 'R2 Storage', 'AWS Integration'],
      path: '/deployment-steps',
      status: 'live'
    },
    {
      id: 'architecture',
      title: '🏗️ Architecture',
      description: 'Interactive system architecture diagram',
      features: ['System overview', 'Component flow', 'Tech stack'],
      path: '/architecture',
      status: 'live'
    },
    {
      id: 'domains',
      title: '🌐 Domain Manager Enhanced',
      description: 'Advanced domain management with live activity monitoring',
      features: ['Real-time activity log', 'Enhanced DNS setup', 'Copy-to-clipboard', 'Domain validation'],
      path: '/domains-enhanced',
      status: 'live'
    },
    {
      id: 'domains-super',
      title: '🚀 Domain Manager Super',
      description: 'Ultimate domain management with complete visibility',
      features: ['Step-by-step visualization', 'Debug panel', 'DNS tracker', 'Activity monitoring'],
      path: '/domains-super',
      status: 'live'
    },
    {
      id: 'links',
      title: '🔗 Links Directory',
      description: 'Complete directory of all system URLs',
      features: ['All endpoints', 'Documentation', 'Quick access'],
      path: '/links',
      status: 'live'
    }
  ];

  const setupGuides = [
    {
      title: 'AWS Setup',
      description: 'Complete AWS account setup wizard',
      path: '/aws-signup',
      icon: '☁️'
    },
    {
      title: 'SES Setup',
      description: 'Configure AWS SES for email sending',
      path: '/ses-setup',
      icon: '✉️'
    },
    {
      title: 'IAM Permissions',
      description: 'Setup IAM roles and permissions',
      path: '/iam-setup',
      icon: '🔐'
    },
    {
      title: 'Project Structure',
      description: 'Explore the codebase structure',
      path: '/project-files',
      icon: '📂'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 to-purple-700 p-12">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-6xl mx-auto text-center"
        >
          <motion.h1
            className="text-7xl font-bold mb-6"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            🚀 Happy Human Tools
          </motion.h1>
          <p className="text-3xl opacity-95 mb-8">
            Production-Ready Tools Built with CloudFlare & AWS
          </p>
          <div className="text-xl opacity-90 max-w-3xl mx-auto">
            A complete email platform, file management system, and deployment infrastructure -
            all built with modern cloud technologies.
          </div>
        </motion.div>
      </div>

      {/* Main Tools Section */}
      <div className="max-w-7xl mx-auto p-8">
        <motion.h2
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-4xl font-bold text-center mb-12"
        >
          🛠️ Available Tools
        </motion.h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {tools.map((tool, index) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700 rounded-xl p-6 hover:border-blue-500 transition-all hover:shadow-xl hover:shadow-blue-500/20 cursor-pointer"
              onClick={() => navigate(tool.path)}
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-2xl font-bold">{tool.title}</h3>
                {tool.status === 'live' && (
                  <span className="bg-green-500/20 text-green-400 px-2 py-1 rounded-full text-xs font-semibold">
                    LIVE
                  </span>
                )}
              </div>

              <p className="text-gray-400 mb-4">{tool.description}</p>

              <ul className="space-y-2">
                {tool.features.map((feature, i) => (
                  <li key={i} className="flex items-center text-sm text-gray-300">
                    <span className="text-blue-400 mr-2">✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button className="w-full mt-6 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg font-semibold transition-colors">
                Open Tool →
              </button>
            </motion.div>
          ))}
        </div>

        {/* Setup Guides Section */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mb-16"
        >
          <h2 className="text-4xl font-bold text-center mb-12">📚 Setup Guides</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {setupGuides.map((guide, index) => (
              <motion.div
                key={guide.path}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 hover:border-purple-500 transition-all cursor-pointer"
                onClick={() => navigate(guide.path)}
              >
                <div className="text-3xl mb-3">{guide.icon}</div>
                <h3 className="text-lg font-semibold mb-2">{guide.title}</h3>
                <p className="text-sm text-gray-400">{guide.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tech Stack */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-gradient-to-br from-purple-900/20 to-blue-900/20 border border-purple-500/30 rounded-xl p-8 text-center"
        >
          <h3 className="text-3xl font-bold mb-6">⚡ Powered By Modern Tech</h3>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-2xl mb-2">☁️</div>
              <div className="font-semibold">CloudFlare</div>
              <div className="text-sm text-gray-400">Workers & R2</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-2xl mb-2">📧</div>
              <div className="font-semibold">AWS SES</div>
              <div className="text-sm text-gray-400">Email Platform</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-2xl mb-2">⚛️</div>
              <div className="font-semibold">React</div>
              <div className="text-sm text-gray-400">Frontend</div>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <div className="text-2xl mb-2">🎨</div>
              <div className="font-semibold">Tailwind</div>
              <div className="text-sm text-gray-400">Styling</div>
            </div>
          </div>

          <p className="text-lg opacity-80">
            Everything you need to build and deploy production applications
          </p>
        </motion.div>

        {/* Quick Stats */}
        <div className="mt-16 text-center">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
              className="bg-slate-800/50 rounded-xl p-6"
            >
              <div className="text-4xl font-bold text-blue-400 mb-2">6+</div>
              <div className="text-gray-400">Production Tools</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.7 }}
              className="bg-slate-800/50 rounded-xl p-6"
            >
              <div className="text-4xl font-bold text-green-400 mb-2">100%</div>
              <div className="text-gray-400">Cloud Native</div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-slate-800/50 rounded-xl p-6"
            >
              <div className="text-4xl font-bold text-purple-400 mb-2">Ready</div>
              <div className="text-gray-400">To Deploy</div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};